import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { ProductType } from "@obg/core.config";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import { DeviceConfigService, PlatformService } from "@obg/ngx.global";
import { SignalrAction } from "@obg/ngx.real-time.signalr";
import { merge, Observable, of, timer } from "rxjs";
import {
	bufferTime,
	catchError,
	filter,
	map,
	mergeMap,
	pairwise,
	startWith,
	switchMap,
	takeUntil,
	withLatestFrom,
} from "rxjs/operators";

import { APP_CONTEXT_ACTION_TYPE } from "../app-context/app-context.action";
import { AppError } from "../app-error/app-error.model";
import { AUTH_ACTION_TYPE } from "../auth/auth.action";
import { SessionStartPayload } from "../auth/auth.model";
import { AuthSelector } from "../auth/auth.selector";
import { MessageScopeType, MessageType } from "../message/message.model";
import { MessageService } from "../message/message.service";
import { CoreState } from "../state";

import { WALLET_ACTION_TYPE, WalletAction } from "./wallet.action";
import { WalletConfigService } from "./wallet.config";
import { WALLET_MESSAGE_CODES, WALLET_MESSAGE_CONTEXT } from "./wallet.const";
import { BalanceTimestamp, TransactionFilter, TransactionTotalsRequest } from "./wallet.model";
import { WalletSelector } from "./wallet.selector";
import { WalletService } from "./wallet.service";
/**
 * @deprecated [fabric-deprecated] - Use wallet-service from app-wallet instead
 */
@Injectable()
export class WalletEffect {
	/**
	 * @deprecated [fabric-deprecated] - Use WalletAppCommand.GetWalletBalance instead
	 */
	getBalance$ = createEffect(() =>
		this.actions$.pipe(
			ofType(WALLET_ACTION_TYPE.getBalance),
			filter(() => this.config.getBalanceEnabled()),
			switchMap(() =>
				this.service.getBalance().pipe(
					map((response) => this.action.getBalanceSuccess(response)),
					catchError((response: AppError) => of(this.action.getBalanceFail(response)))
				)
			)
		)
	);

	sessionStart$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionStart),
			filter(() => this.config.getBalanceEnabled()),
			map((x: ActionWithPayload<SessionStartPayload>) => x.payload),
			map((session) =>
				this.config.isRealtimeBalanceEnabled()
					? this.action.enableRealtimeBalance(session.token)
					: this.action.enablePolledBalance()
			)
		)
	);

	updateRealtimeStatus$: Observable<Action> = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(WALLET_ACTION_TYPE.updateRealtimeStatus),
				map(() => SignalrAction.subscribeToCustomerTransmitChannel(this.config.getWalletUpdateTopic()))
			)
		)
	);

	realtimeBalance$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(SignalrAction.customerTransmitChannelMessage),
				filter(({ channel }) => channel === this.config.getWalletUpdateTopic()),
				startWith({ message: "" }),
				map(({ message }) => (message ? JSON.parse(message) : { TimeStamp: 0 }) as BalanceTimestamp),
				pairwise(),
				filter(([a, b]) => new Date(b.TimeStamp) > new Date(a.TimeStamp)),
				bufferTime(this.deviceConfigService.isDesktop() ? this.config.getPolledBalanceInterval() : 0),
				filter((x) => !!x.length),
				map(() => this.action.getBalance())
			)
		)
	);

	startRealtime$: Observable<Action> = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(WALLET_ACTION_TYPE.enableRealtimeBalance),
				map(() => this.action.updateRealtimeStatus())
			)
		)
	);

	realtimeError$: Observable<Action> = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(SignalrAction.connectCustomerTransmitFail),
				map(() => this.action.enablePolledBalance())
			)
		)
	);
	/**
	 * @deprecated [fabric-deprecated] - WalletAppCommand.StartPaymentStatusPolling / WalletAppCommand.StopPaymentStatusPolling instead
	 *
	 * Polls server within configured interval, dispatches fail actions if there's request errors
	 */
	startPolling$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(WALLET_ACTION_TYPE.enablePolledBalance),
				withLatestFrom(this.store.select(AuthSelector.isAuthenticated)),
				filter(([, isAuthenticated]) => isAuthenticated),
				switchMap(() =>
					timer(0, this.config.getPolledBalanceInterval()).pipe(
						switchMap(() =>
							this.service.getBalance().pipe(
								map((response) => this.action.getBalanceSuccess(response)),
								catchError((response: AppError) => of(this.action.getBalanceFail(response)))
							)
						),
						takeUntil(this.actions$.pipe(ofType(WALLET_ACTION_TYPE.disablePolledBalance)))
					)
				)
			)
		)
	);

	sessionEnd$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map(() => batchActions(this.action.disablePolledBalance(), this.action.reset()))
		)
	);
	/**
	 * @deprecated [fabric-deprecated] - Use WalletAppCommand.GetBetWinSummary instead
	 */
	getBetWinSummary$: Observable<ActionWithPayload> = createEffect(() =>
		this.actions$.pipe(
			ofType(WALLET_ACTION_TYPE.getBetWinSummary),
			map((x: ActionWithPayload<number>) => x.payload),
			switchMap((minutes) =>
				this.service.getBetWinSummary(minutes).pipe(
					map((response) => this.action.getBetWinSummarySuccess(response)),
					catchError((response: AppError) => of(this.action.getBetWinSummaryFail(response)))
				)
			)
		)
	);

	getTransaction$ = createEffect(() =>
		this.actions$.pipe(
			ofType(WALLET_ACTION_TYPE.getTransaction),
			map((x: ActionWithPayload<string>) => x.payload),
			switchMap((transactionId) =>
				this.service.getTransaction(transactionId).pipe(
					map((response) => this.action.getTransactionSuccess(response)),
					catchError((response: AppError) => of(this.action.getTransactionFail(response)))
				)
			)
		)
	);

	getTransactions$ = createEffect(() =>
		this.actions$.pipe(
			ofType(WALLET_ACTION_TYPE.getTransactions),
			map((x: ActionWithPayload<TransactionFilter>) => x.payload),
			switchMap((transactionFilter) =>
				this.service.getTransactions(transactionFilter).pipe(
					map((response) => this.action.getTransactionsSuccess(response)),
					catchError((response: AppError) => of(this.action.getTransactionsFail(response)))
				)
			)
		)
	);
	/**
	 * @deprecated [fabric-deprecated] - Use WalletAppCommand.GetTransactionTotals instead
	 */
	getTransactionTotals$ = createEffect(() =>
		this.actions$.pipe(
			ofType(WALLET_ACTION_TYPE.getTransactionTotals),
			map((x: ActionWithPayload<TransactionTotalsRequest>) => x.payload),
			mergeMap((request) =>
				this.service.getTransactionTotals(request.filter).pipe(
					map((response) => this.action.getTransactionTotalsSuccess({ id: request.id, result: response })),
					catchError((response: AppError) =>
						of(this.action.getTransactionTotalsFail({ id: request.id, error: response }))
					)
				)
			)
		)
	);

	updateBalanceByProduct$ = createEffect(() =>
		this.actions$.pipe(
			ofType(APP_CONTEXT_ACTION_TYPE.setProduct),
			map((x: ActionWithPayload<ProductType>) => x.payload),
			switchMap((productType) =>
				this.store.pipe(
					this.selector.getBalances$(),
					concatLatestFrom(() => this.store.select(AuthSelector.isAuthenticated)),
					filter(([, isAuthenticated]) => isAuthenticated),
					map(([balance]) =>
						this.action.updateCurrentProductBalance(this.service.getBalanceByProduct(productType, balance))
					)
				)
			)
		)
	);

	getWithdrawalRestriction$ = createEffect(() =>
		this.actions$.pipe(
			ofType(WALLET_ACTION_TYPE.getWithdrawalRestrictions),
			switchMap(() =>
				this.service.getWithdrawalRestriction().pipe(
					map((response) => this.action.getWithdrawalRestrictionSuccess(response)),
					catchError((response: AppError) => of(this.action.getWithdrawalRestrictionFail(response)))
				)
			)
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[WALLET_ACTION_TYPE.getBalanceFail]: WALLET_MESSAGE_CONTEXT.getBalance,
				[WALLET_ACTION_TYPE.getBetWinSummaryFail]: WALLET_MESSAGE_CONTEXT.getBetWinSummary,
				[WALLET_ACTION_TYPE.getTransactionFail]: WALLET_MESSAGE_CONTEXT.getTransaction,
				[WALLET_ACTION_TYPE.getTransactionsFail]: WALLET_MESSAGE_CONTEXT.getTransactions,
				[WALLET_ACTION_TYPE.getWithdrawalRestrictionsFail]: WALLET_MESSAGE_CONTEXT.getWithdrawalRestriction,
			}),
			this.messageService.mapClearByContext$({
				[WALLET_ACTION_TYPE.getBetWinSummary]: WALLET_MESSAGE_CONTEXT.getBetWinSummary,
				[WALLET_ACTION_TYPE.getTransaction]: WALLET_MESSAGE_CONTEXT.getTransaction,
				[WALLET_ACTION_TYPE.getTransactions]: WALLET_MESSAGE_CONTEXT.getTransactions,
				[WALLET_ACTION_TYPE.getWithdrawalRestrictions]: WALLET_MESSAGE_CONTEXT.getWithdrawalRestriction,
			}),
			this.messageService.map$({
				[WALLET_MESSAGE_CODES.transactions.failure]: {
					context: WALLET_MESSAGE_CONTEXT.getTransactions,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: WALLET_MESSAGE_CODES.transactions.failure,
				},
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: WalletAction,
		private config: WalletConfigService,
		private service: WalletService,
		private store: Store<CoreState>,
		private selector: WalletSelector,
		private messageService: MessageService,
		private platformService: PlatformService,
		private deviceConfigService: DeviceConfigService
	) {}
}
